import { defineComponent, ref, computed, watch, createVNode, mergeProps, Fragment } from "vue";
import { getPrefixCls } from "../../_utils/global-config.js";
import { INPUT_EVENTS } from "../../_utils/constant.js";
import { isArray } from "../../_utils/is.js";
import InputTag from "../../input-tag/index.js";
import AIconHover from "../icon-hover.js";
import IconDown from "../../icon/icon-down/index.js";
import IconLoading from "../../icon/icon-loading/index.js";
import IconClose from "../../icon/icon-close/index.js";
import IconExpand from "../../icon/icon-expand/index.js";
import IconSearch from "../../icon/icon-search/index.js";
import { omit } from "../../_utils/omit.js";
import pick from "../../_utils/pick.js";
var SelectView = defineComponent({
  name: "SelectView",
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [Object, Array]
    },
    inputValue: {
      type: String
    },
    placeholder: String,
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    opened: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "medium"
    },
    bordered: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    allowClear: {
      type: Boolean,
      default: false
    },
    allowCreate: {
      type: Boolean,
      default: false
    },
    allowSearch: {
      type: Boolean,
      default: (props) => isArray(props.modelValue)
    },
    maxTagCount: {
      type: Number,
      default: 0
    },
    formatLabel: {
      type: Function
    },
    retainInputValue: {
      type: Boolean,
      default: false
    },
    onChange: Function,
    onInputValueChange: Function,
    onFocus: Function,
    onBlur: Function,
    onRemove: Function,
    onClear: Function
  },
  emits: ["update:modelValue", "update:inputValue", "change", "inputValueChange", "focus", "blur", "remove", "clear"],
  setup(props, {
    emit,
    slots,
    attrs
  }) {
    const prefixCls = getPrefixCls("select-view");
    const inputRef = ref();
    const focused = ref(false);
    const validValue = computed(() => {
      if (props.multiple && !isArray(props.modelValue)) {
        return props.modelValue ? [props.modelValue] : [];
      }
      if (!props.multiple && isArray(props.modelValue)) {
        return props.modelValue[0];
      }
      return props.modelValue;
    });
    const mergedFocused = computed(() => focused.value || props.opened);
    const isEmptyValue = computed(() => isArray(validValue.value) ? validValue.value.length === 0 : !validValue.value);
    const canFocusInput = computed(() => props.allowSearch || props.allowCreate || isArray(validValue.value));
    const showInput = computed(() => canFocusInput.value && mergedFocused.value);
    const showClearBtn = computed(() => props.allowClear && !props.disabled && !isEmptyValue.value);
    const mergedInputValue = computed(() => {
      var _a, _b, _c;
      if (props.inputValue) {
        return props.inputValue;
      }
      if (props.retainInputValue && !isArray(validValue.value) && ((_a = validValue.value) == null ? void 0 : _a.label)) {
        return (_c = (_b = props.formatLabel) == null ? void 0 : _b.call(props, validValue.value)) != null ? _c : validValue.value.label;
      }
      return "";
    });
    const mergedPlaceholder = computed(() => {
      var _a;
      if (isArray(validValue.value)) {
        return "";
      }
      if (canFocusInput.value && !isEmptyValue.value) {
        return (_a = validValue.value) == null ? void 0 : _a.label;
      }
      return props.placeholder;
    });
    const handleInput = (e) => {
      const {
        value
      } = e.target;
      emit("inputValueChange", value, e);
    };
    const handleInputValueChange = (value, e) => {
      emit("inputValueChange", value, e);
    };
    const handleMousedown = (e) => {
      if (inputRef.value && e.target !== inputRef.value) {
        e.preventDefault();
        inputRef.value.focus();
      }
    };
    const handleFocus = () => {
      focused.value = true;
      emit("focus");
    };
    const handleBlur = () => {
      focused.value = false;
      emit("blur");
    };
    const handleRemove = (tag) => {
      emit("remove", tag);
    };
    const handleClear = (e) => {
      emit("clear", e);
    };
    watch(() => props.opened, (value) => {
      if (inputRef.value) {
        if (value) {
          inputRef.value.focus();
        } else {
          inputRef.value.blur();
        }
      }
    });
    const renderIcon = () => {
      var _a, _b, _c, _d;
      if (props.loading) {
        return (_b = (_a = slots.loadingIcon) == null ? void 0 : _a.call(slots)) != null ? _b : createVNode(IconLoading, null, null);
      }
      if (props.allowSearch && focused.value) {
        return (_d = (_c = slots.searchIcon) == null ? void 0 : _c.call(slots)) != null ? _d : createVNode(IconSearch, null, null);
      }
      if (slots.arrowIcon) {
        return slots.arrowIcon();
      }
      if (canFocusInput.value) {
        return createVNode(IconExpand, {
          "style": {
            transform: "rotate(-45deg)"
          }
        }, null);
      }
      return createVNode(IconDown, {
        "class": `${prefixCls}-arrow-icon`
      }, null);
    };
    const renderSuffix = () => createVNode(Fragment, null, [showClearBtn.value && createVNode("span", {
      "class": `${prefixCls}-clear-btn`,
      "onClick": handleClear,
      "onMousedown": (e) => e.stopPropagation()
    }, [createVNode(AIconHover, null, {
      default: () => [createVNode(IconClose, null, null)]
    })]), createVNode("span", {
      "class": `${prefixCls}-icon`
    }, [renderIcon()])]);
    const renderLabel = () => {
      var _a, _b, _c, _d, _e;
      if (isArray(validValue.value)) {
        return null;
      }
      return (_e = (_c = (_a = slots.label) == null ? void 0 : _a.call(slots, {
        data: validValue.value
      })) != null ? _c : (_b = props.formatLabel) == null ? void 0 : _b.call(props, validValue.value)) != null ? _e : (_d = validValue.value) == null ? void 0 : _d.label;
    };
    const cls = computed(() => [prefixCls, `${prefixCls}-${isArray(validValue.value) ? "multiple" : "single"}`, `${prefixCls}-size-${props.size}`, {
      [`${prefixCls}-search`]: props.allowSearch,
      [`${prefixCls}-opened`]: props.opened,
      [`${prefixCls}-focus`]: mergedFocused.value,
      [`${prefixCls}-disabled`]: props.disabled,
      [`${prefixCls}-error`]: props.error,
      [`${prefixCls}-bordered`]: props.bordered
    }]);
    const wrapperAttrs = omit(attrs, INPUT_EVENTS);
    const inputAttrs = pick(attrs, INPUT_EVENTS);
    const renderSingle = () => {
      return createVNode("span", mergeProps({
        "class": cls.value,
        "onMousedown": handleMousedown
      }, wrapperAttrs), [slots.prefix && createVNode("span", {
        "class": `${prefixCls}-prefix`
      }, [slots.prefix()]), createVNode("input", mergeProps({
        "ref": "inputRef",
        "class": [`${prefixCls}-input`, {
          [`${prefixCls}-input-hidden`]: !showInput.value && !isEmptyValue.value
        }],
        "value": mergedInputValue.value,
        "readonly": !canFocusInput.value,
        "placeholder": mergedPlaceholder.value,
        "disabled": props.disabled,
        "onInput": handleInput,
        "onFocus": handleFocus,
        "onBlur": handleBlur
      }, inputAttrs), null), createVNode("span", {
        "class": [`${prefixCls}-value`, {
          [`${prefixCls}-value-hidden`]: showInput.value || isEmptyValue.value
        }]
      }, [renderLabel()]), createVNode("span", {
        "class": `${prefixCls}-suffix`
      }, [renderSuffix()])]);
    };
    const renderMultiple = () => {
      return createVNode(InputTag, mergeProps({
        "ref": "inputRef",
        "modelValue": validValue.value,
        "formatTag": props.formatLabel,
        "inputValue": props.inputValue,
        "placeholder": props.placeholder,
        "disabled": props.disabled,
        "allowClear": props.allowClear,
        "size": props.size,
        "focused": mergedFocused.value,
        "error": props.error,
        "maxTagCount": props.maxTagCount,
        "retainInputValue": true,
        "hideSuffixOnClear": true,
        "onInputValueChange": handleInputValueChange,
        "onFocus": handleFocus,
        "onBlur": handleBlur,
        "onClear": handleClear,
        "onRemove": handleRemove
      }, attrs), {
        prefix: slots.prefix,
        suffix: renderSuffix,
        tag: slots.label
      });
    };
    const render = () => {
      if (isArray(validValue.value)) {
        return renderMultiple();
      }
      return renderSingle();
    };
    return {
      inputRef,
      render
    };
  },
  methods: {
    focus() {
      if (this.inputRef) {
        this.inputRef.focus();
      }
    },
    blur() {
      if (this.inputRef) {
        this.inputRef.blur();
      }
    }
  },
  render() {
    return this.render();
  }
});
export { SelectView as default };
